import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { useSeo } from "@/lib/seo";
import { Slider } from "@/components/ui/slider";

const NICHES = ["general", "finance", "tech", "business", "education", "gaming", "entertainment", "lifestyle", "fitness", "beauty"];
const fmt = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const TOOLTIP_STYLE = { background: "var(--card)", border: "1px solid var(--border-c)", borderRadius: 12, color: "var(--text)" };

export default function EarningsCalculator() {
  useSeo("YouTube Earnings Calculator", "Estimate your YouTube AdSense earnings from daily views, CPM and niche with a live projection chart.");
  const [views, setViews] = useState(10000);
  const [cpm, setCpm] = useState(4);
  const [niche, setNiche] = useState("general");
  const [result, setResult] = useState(null);

  const calc = useCallback(async () => {
    const res = await api.post("/earnings", { dailyViews: views, cpm, niche });
    setResult(res.data);
  }, [views, cpm, niche]);

  useEffect(() => {
    const t = setTimeout(calc, 250);
    return () => clearTimeout(t);
  }, [calc]);

  return (
    <ToolShell slug="earnings-calculator" title="Earnings Calculator" tagline="Drag the sliders and watch your estimated AdSense revenue update live." hasResults={!!result}>
      <div className="surface rounded-[20px] p-6 sm:p-8 space-y-8">
        <div>
          <div className="flex justify-between mb-3">
            <label className="text-sm font-semibold">Daily views</label>
            <span className="text-sm font-bold text-[var(--glow)]" data-testid="views-value">{views.toLocaleString()}</span>
          </div>
          <Slider data-testid="views-slider" value={[views]} onValueChange={(v) => setViews(v[0])} min={100} max={1000000} step={100} />
        </div>
        <div>
          <div className="flex justify-between mb-3">
            <label className="text-sm font-semibold">CPM (cost per 1,000 impressions)</label>
            <span className="text-sm font-bold text-[var(--glow)]" data-testid="cpm-value">${cpm.toFixed(2)}</span>
          </div>
          <Slider data-testid="cpm-slider" value={[cpm]} onValueChange={(v) => setCpm(v[0])} min={0.5} max={30} step={0.5} />
        </div>
        <div>
          <label className="text-sm font-semibold block mb-3">Niche</label>
          <div className="flex flex-wrap gap-2">
            {NICHES.map((n) => (
              <button
                key={n}
                data-testid={`niche-${n}`}
                onClick={() => setNiche(n)}
                className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${
                  niche === n ? "bg-[var(--glow)] text-white" : "surface text-[var(--muted)] hover:text-[var(--text)]"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      {result && (
        <div className="mt-8 fade-up">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Daily", value: result.daily, testId: "earnings-daily" },
              { label: "Monthly", value: result.monthly, testId: "earnings-monthly" },
              { label: "Yearly", value: result.yearly, testId: "earnings-yearly" },
            ].map((s) => (
              <div key={s.label} className="surface rounded-[20px] p-5">
                <p className="text-[var(--muted)] text-xs uppercase tracking-[0.15em]">{s.label}</p>
                <p className="font-display font-black text-xl sm:text-3xl mt-1 text-[var(--success)]" data-testid={s.testId}>{fmt(s.value)}</p>
              </div>
            ))}
          </div>
          <div className="surface rounded-[20px] p-6 mt-4">
            <p className="text-sm font-semibold mb-4">12-month projection <span className="text-[var(--muted)] font-normal">(RPM {fmt(result.rpm)} after 45% platform share)</span></p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={result.series}>
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7C5CFF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#7C5CFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="var(--muted)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(m) => `M${m}`} />
                  <YAxis stroke="var(--muted)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [fmt(v), "Earnings"]} labelFormatter={(m) => `Month ${m}`} />
                  <Area type="monotone" dataKey="earnings" stroke="#7C5CFF" strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </ToolShell>
  );
}
